import type { ReactNode } from 'react';
import type { UserAccess } from '@features/user-access/types';

import { useNavigate } from 'react-router';
import { useMemo, useEffect, useContext, createContext } from 'react';

import { FatalError } from '@shared/components/fatal-error';
import { SplashScreen } from '@shared/components/splash-screen';

import { useToken } from '@features/user-access/hooks/use-token';
import { useUserAccessQuery } from '@features/user-access/api/user-access-api';

type UserContextType = {
  userAccess: UserAccess;
  refetchUserAccess: () => void;
  token: string;
  setToken: (token: string | null) => void;
};

const UPDATE_USER_INTERVAL = 60 * 1000;

// 1. Créer le Context avec une valeur par défaut de `null`
//    C'est normal à ce niveau, car en dehors du Provider, il n'y a pas de données.
const UserContext = createContext<UserContextType | null>(null);

// 2. Créer le Provider
export const UserProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { token, setToken } = useToken();
  const {
    data: userAccess,
    isLoading,
    isError,
    refetch,
  } = useUserAccessQuery(undefined, {
    pollingInterval: UPDATE_USER_INTERVAL,
    skip: !token, // On ne fait la requête que si on a un token
  });

  const value = useMemo(
    () => ({
      // On doit s'assurer que companySettings est défini ici
      // Mais comme on le vérifie juste après, on peut utiliser un "non-null assertion" (!)
      // ou le laisser potentiellement undefined dans la memo, la vérification ci-dessous fera le travail.
      userAccess: userAccess!,
      refetchUserAccess: refetch,
      token: token!,
      setToken,
    }),
    [userAccess, refetch, token, setToken]
  );

  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [navigate, token]);

  if (isLoading) {
    return <SplashScreen />;
  }

  // En cas d'erreur critique qui empêche l'app de fonctionner
  if (!token || isError || !userAccess) {
    // Vous pouvez logguer l'erreur `error` ici
    return <FatalError />;
  }

  // 3. Si tout va bien, on fournit les données via le Provider
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

type UseUserAccessType = {
  userAccess: UserAccess;
  refetchUserAccess: () => void;
  token: string;
  logout: () => void;
};

// 4. Créer le hook final `useUser`
export const useUser = (): UseUserAccessType => {
  const context = useContext(UserContext);

  // Le hook lèvera une erreur s'il est utilisé hors du Provider,
  // ce qui garantit que la valeur n'est jamais `null` là où on l'utilise.
  if (context === null) {
    throw new Error("useUser() doit être utilisé à l'intérieur d'un UserProvider");
  }

  return {
    ...context,
    logout: () => context.setToken(null),
  };
};
